import { FC, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./modal.module.scss";
import { modalProps } from "./modal.type";
import { Icon, Typography } from "top-brains-ui";
import Button from "../button/Button";
import { BTN_COLORS } from "@src/styles/ColorConstants";
import { ZoomIn, ZoomOut } from "lucide-react";

const PortalModal: FC<modalProps> = ({
  customHeader,
  body = <>Your Content</>,
  customFooter,
  headerTitle,
  primaryButtonText = "Save",
  secondaryButtonText = "Cancel",
  closeOnOutsideClick = false,
  showPrimaryButton = true,
  showSecondaryButton = true,
  showCloseButton = true,
  showHeader = true,
  showFooter = true,
  height = "300px",
  width = "500px",
  maxHeight,
  isOpen = true,
  onClose = () => {},
  onPrimaryButtonClick = () => {},
  onSecondaryButtonClick = () => {},
  variant = "default",
  showZoomInButton = false,
  showZoomOutButton = false,
  scale = 1,
  setScale = () => {},
  overlayHeight = "",
}) => {
  const [open, setOpen] = useState<boolean>(isOpen || true);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const handleClose = () => {
    onClose?.();
    setOpen(false);
  };

  const handlePrimarybuttonClick = () => {
    handleClose();
    onPrimaryButtonClick?.();
  };

  const handleSecondaryButtonClick = () => {
    handleClose();
    onSecondaryButtonClick?.();
  };

  // 🔑 Render modal content
  const modalContent = (
    <div
      className={`${styles.modalContainer} ${
        variant === "website" ? styles.websiteVariant : ""
      }`}
      style={{
        display: open ? "flex" : "none",
        position: "fixed",
        zIndex: "99999",
        height: overlayHeight,
      }}
      onClick={() => {
        if (closeOnOutsideClick) handleClose();
      }}
    >
      <div
        className={styles.modalBody}
        style={{
          height,
          width,
          maxHeight,
          paddingBottom: showFooter ? undefined : "0",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (
          <div className={styles.modalHeader}>
            {customHeader ? (
              customHeader
            ) : (
              <div className={styles.headerContent}>
                <Typography fontWeight="medium" fontSize={"14px"}>
                  {headerTitle ? headerTitle : ""}
                </Typography>
                {showZoomInButton || showZoomOutButton ? (
                  <div className={styles.zoomButtons}>
                    {showZoomInButton ? (
                      <button
                        onClick={handleZoomIn}
                        disabled={scale >= 3}
                        style={{
                          cursor: scale >= 3 ? "not-allowed" : "",
                        }}
                      >
                        <ZoomIn color={scale >= 3 ? "#575757" : "#B2B2B2"} />
                      </button>
                    ) : null}
                    <p className={styles.scale}>
                      {scale == 1.0 ? 1 : scale.toFixed(1)}
                    </p>
                    {showZoomOutButton ? (
                      <button
                        onClick={handleZoomOut}
                        disabled={scale <= 0.5}
                        style={{
                          cursor: scale <= 0.5 ? "not-allowed" : "",
                        }}
                      >
                        <ZoomOut color={scale <= 0.5 ? "#575757" : "#B2B2B2"} />
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {showCloseButton && (
                  <Icon
                    name="close"
                    height={14}
                    width={14}
                    onClick={handleClose}
                    color="#B2B2B2"
                    className={styles.closeButton}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <div
          className={styles.modalContent}
          style={{
            height: showFooter ? undefined : "calc(100% - 48px)",
            flexGrow: showFooter ? undefined : 1,
          }}
        >
          {body}
        </div>

        {showFooter && (
          <div className={styles.modalFooter}>
            {customFooter ? (
              customFooter
            ) : (
              <div className={styles.footerContent}>
                {showSecondaryButton && (
                  <Button
                    variant="secondary"
                    size="medium"
                    onClick={handleSecondaryButtonClick}
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {showPrimaryButton && (
                  <Button
                    variant="primary"
                    size="medium"
                    style={{ color: BTN_COLORS.BTN_TEXT_COLOR }}
                    onClick={handlePrimarybuttonClick}
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // 🔑 Use portal to render into #modal-root
  return ReactDOM.createPortal(
    modalContent,
    document.getElementById("modal-root")!
  );
};

export default PortalModal;
